import React, { memo, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useAuth } from "../../contexts/JWTContext";
import Logo from "../Logo";
import { LUX_PARTNERS } from "../../data/luxCatalog";
import styles from "./Footer.module.css";

import b002 from "../../assets/bankicon/002.webp";
import b004 from "../../assets/bankicon/004.webp";
import b006 from "../../assets/bankicon/006.webp";
import b011 from "../../assets/bankicon/011.webp";
import b014 from "../../assets/bankicon/014.webp";
import b025 from "../../assets/bankicon/025.webp";
import b030 from "../../assets/bankicon/030.webp";
import b034 from "../../assets/bankicon/034.webp";
import b069 from "../../assets/bankicon/069.webp";
import bPromptpay from "../../assets/bankicon/promptpay.webp";
import bTrue from "../../assets/bankicon/true.webp";

const BANKS = [b004, b014, b002, b006, b025, b011, b030, b034, b069, bPromptpay, bTrue];

const openLogin = () => window.dispatchEvent(new CustomEvent("open-login-modal"));

const Footer = memo(({ onContactClick }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const requireAuth = useCallback(
    (path) => {
      if (!isAuthenticated) return openLogin();
      navigate(path);
    },
    [isAuthenticated, navigate],
  );

  const promoPath = isAuthenticated ? "/menu?tab=autoPromo" : "/promotion";
  const tab = new URLSearchParams(location.search).get("tab");
  const isPromo =
    location.pathname === "/promotion" || (location.pathname === "/menu" && tab === "autoPromo");
  const isWallet = location.pathname === "/menu" && (tab === "deposit" || tab === "withdraw");
  const isAccount = location.pathname === "/menu" && !isPromo && !isWallet;
  const year = new Date().getFullYear();

  return (
    <>
      {/* ================= site footer (all sizes) ================= */}
      <footer className={styles.site}>
        <div className={styles.siteGlow} aria-hidden="true" />
        <div className="lux-container">
          <div className={styles.partners}>
            <span className="lux-eyebrow">Official Partners</span>
            <div className={styles.partnerTrack}>
              <div className={styles.partnerRow}>
                {[...LUX_PARTNERS, ...LUX_PARTNERS].map((p, i) => (
                  <img
                    key={`${p.id}-${i}`}
                    src={p.img}
                    alt={i < LUX_PARTNERS.length ? p.id : ""}
                    aria-hidden={i >= LUX_PARTNERS.length}
                    loading="lazy"
                  />
                ))}
              </div>
            </div>
          </div>

          <hr className="lux-divider" />

          <div className={styles.grid}>
            <div className={styles.brand}>
              <Logo />
              <p>
                ศูนย์รวมความบันเทิงระดับพรีเมียม คาสิโนสด สล็อต กีฬา ยิงปลา
                ครบจบในที่เดียว ระบบฝาก-ถอนอัตโนมัติ ดูแลตลอด 24 ชั่วโมง
              </p>
              <div className={styles.badges}>
                <span>
                  <Icon icon="solar:shield-check-bold" width={16} /> SSL Secured
                </span>
                <span>
                  <Icon icon="solar:bolt-bold" width={16} /> Auto 30s
                </span>
                <span>18+</span>
              </div>
            </div>

            <div className={styles.links}>
              <h4>เกม</h4>
              <NavLink to="/livecasino">คาสิโนสด</NavLink>
              <NavLink to="/slot">สล็อต</NavLink>
              <NavLink to="/sportbook">กีฬา</NavLink>
              <NavLink to="/card">ยิงปลา</NavLink>
              <NavLink to="/keno">หวย / คีโน</NavLink>
            </div>

            <div className={styles.links}>
              <h4>บริการ</h4>
              <NavLink to={promoPath}>โปรโมชั่น</NavLink>
              <NavLink to="/reviews">รีวิวจากลูกค้า</NavLink>
              <NavLink to="/blog">บทความ</NavLink>
              <button type="button" onClick={onContactClick}>
                ติดต่อเรา
              </button>
            </div>

            <div className={styles.banks}>
              <h4>รองรับทุกธนาคาร</h4>
              <div className={styles.bankGrid}>
                {BANKS.map((src, i) => (
                  <img key={i} src={src} alt="" loading="lazy" />
                ))}
              </div>
            </div>
          </div>

          <div className={styles.copy}>
            © {year} All rights reserved · เล่นอย่างมีความรับผิดชอบ
          </div>
        </div>
      </footer>

      {/* ================= mobile dock ================= */}
      <nav className={styles.dock} aria-label="เมนูหลัก">
        <NavLink
          to="/home"
          className={({ isActive }) => `${styles.dockItem} ${isActive ? styles.on : ""}`}
        >
          <Icon icon="solar:home-2-bold-duotone" className={styles.dockIcon} />
          <span>หน้าแรก</span>
        </NavLink>

        <button
          type="button"
          className={`${styles.dockItem} ${isPromo ? styles.on : ""}`}
          onClick={() => navigate(promoPath)}
        >
          <Icon icon="solar:gift-bold-duotone" className={styles.dockIcon} />
          <span>โปรโมชั่น</span>
        </button>

        <button
          type="button"
          className={`${styles.dockCenter} ${isWallet ? styles.on : ""}`}
          onClick={() => requireAuth("/menu?tab=deposit")}
          aria-label="ฝาก-ถอน"
        >
          <span className={styles.coin}>
            <Icon icon="solar:wallet-money-bold" width={26} />
          </span>
          <span className={styles.coinLabel}>ฝาก-ถอน</span>
        </button>

        <button type="button" className={styles.dockItem} onClick={onContactClick}>
          <Icon icon="solar:chat-round-dots-bold-duotone" className={styles.dockIcon} />
          <span>ติดต่อเรา</span>
        </button>

        {isAuthenticated ? (
          <button
            type="button"
            className={`${styles.dockItem} ${isAccount ? styles.on : ""}`}
            onClick={() => navigate("/menu")}
          >
            <Icon icon="solar:user-circle-bold-duotone" className={styles.dockIcon} />
            <span>บัญชี</span>
          </button>
        ) : (
          <button type="button" className={styles.dockItem} onClick={openLogin}>
            <Icon icon="solar:login-3-bold-duotone" className={styles.dockIcon} />
            <span>เข้าสู่ระบบ</span>
          </button>
        )}
      </nav>
    </>
  );
});

Footer.displayName = "Footer";

export default Footer;
